'use client';

import {
  CoolblueEnergyChip,
  EducationChip,
  HoursChip,
  InternalChip,
  LocationChip,
  RelocationChip,
  SalaryChip
} from '@components/Chips';
import { ChipGroup, freddyTheme, Link, ListItem, Typography } from '@coolblue-development/freddy';
import selectVacancyTrigger from '@coolblue-development/ga4-events/triggers/RecruitmentFilterResults/selectVacancyTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import { useTranslation } from '@i18n/useTranslation';
import { type SearchVacancyItem } from '@server/careers-backend/search';
import { generateWebsiteUrl } from '@utils/url-generators/generateWebsiteUrl';

type VacancyListItemProps = {
  item: SearchVacancyItem;
  index: number;
  totalIndices: number;
};

export const VacancyListItem = ({ item, index, totalIndices }: VacancyListItemProps) => {
  const { trackEvent } = useAnalytics();
  const { subsidiary, language } = useRequestContext();
  const { t } = useTranslation('filterPage.chips');

  const SELECTION_TYPE = 'selected';
  const handleVacancyClick = () => {
    if (!item.id) return;
    trackEvent(
      selectVacancyTrigger({
        selection_type: SELECTION_TYPE,
        option_name: item.id,
        index: index + 1,
        total_indices: totalIndices
      })
    );
  };
  const vacancyUrl = generateWebsiteUrl(item.slug, subsidiary, language);
  return (
    <ListItem>
      <Link
        href={vacancyUrl}
        aria-label={item.title}
        sx={{
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'none'
          },
          '&:hover h3': {
            textDecoration: 'underline'
          }
        }}
        onClick={handleVacancyClick}
      >
        <Typography
          variant='h2'
          component='h3'
          color={freddyTheme.color.font.headingMain}
          mb={{ xs: freddyTheme.spacing[100], md: freddyTheme.spacing[200] }}
        >
          {item.title}
        </Typography>
        <Typography
          mb={{ xs: freddyTheme.spacing[200], md: freddyTheme.spacing[400] }}
          truncateLines={{
            xs: 3,
            md: 4
          }}
        >
          {item.description}
        </Typography>
        <ChipGroup size='small'>
          <LocationChip
            locationValue={item.locations.length > 1 ? t('multipleLocations') : item.locations[0]}
          />
          {item.hours?.gte && item.hours?.lte && (
            <HoursChip hours={{ min: item.hours.gte, max: item.hours.lte }} />
          )}
          <EducationChip educationLevels={item.educationLevels} />
          <SalaryChip salaryValue={item.salaryValue} salaryRange={item.salaryRange} />
          {item.relocationPossible && <RelocationChip />}
          {item.isCoolblueEnergy && <CoolblueEnergyChip />}
          {item.internal && <InternalChip />}
        </ChipGroup>
      </Link>
    </ListItem>
  );
};
